import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";

interface Choice {
    id: number;
    text: string;
    order_num: number;
    is_correct: boolean;
}

interface Question {
    id: number;
    text: string;
    type: string;
    order_num: number;
    image_url?: string | null;
    choices: Choice[];
    original_order: number;
}

export default function TestPreviewPage() {
    const { id } = useParams<{ id: string }>();
    const [testName, setTestName] = useState("");
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            try {
                if (!id) return;

                // 1. Load test info
                const { data: testData, error: testError } = await supabase
                    .from("tests")
                    .select("name")
                    .eq("id", id)
                    .maybeSingle();

                if (testError) throw testError;
                setTestName(testData?.name ?? "");

                // 2. Load questions + choices (explicit FK joins)
                const { data, error } = await supabase
                    .from("test_questions")
                    .select(`
            original_order,
            questions!fk_test_questions_questions (
              id, text, type, order_num, image_url,
              choices!choices_question_id_fkey (
                id, text, order_num, is_correct
              )
            )
          `)
                    .eq("test_id", id)
                    .order("original_order");

                if (error) throw error;

                // 3. Flatten into Question[]
                const merged: Question[] = (data || [])
                    .map((row: any) => {
                        const q = row.questions;
                        return {
                            ...q,
                            choices: (q.choices || []).map((c: any) => ({
                                id: c.id,
                                text: c.text,
                                order_num: c.order_num,
                                is_correct: c.is_correct ?? false,
                            })),
                            original_order: row.original_order,
                        } as Question;
                    })
                    .sort((a, b) => a.original_order - b.original_order);

                setQuestions(merged);
            } catch (err: any) {
                console.error("❌ Error loading test preview:", err.message);
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [id]);

    if (loading) return <p className="p-6">Loading preview...</p>;

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">
                Test Preview – {testName || `Test ${id}`}
            </h1>

            {questions.length === 0 ? (
                <p className="text-gray-600">No questions found for this test.</p>
            ) : (
                <div className="space-y-6">
                    {questions.map((q, idx) => (
                        <div key={q.id} className="bg-white p-4 rounded shadow">
                            <h2 className="font-semibold mb-2">
                                {idx + 1}. {q.text}
                            </h2>

                            {q.image_url && (
                                <img
                                    src={q.image_url}
                                    alt="Question illustration"
                                    className="my-2 max-h-64"
                                />
                            )}

                            <ul className="list-disc pl-6">
                                {q.choices.map((c) => (
                                    <li
                                        key={c.id}
                                        className={c.is_correct ? "font-bold text-green-700" : ""}
                                    >
                                        {c.text} {c.is_correct && "✅"}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
